import { type ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import type { Nurse, NurseInShift } from "../../utils/models";
import { type QuickAddPickSeed, type QuickAddPickWindows, resolvePickSeed, type ShiftWindow } from "../../utils/scheduleConflict";
import { scheduledChipLabel } from "../../utils/scheduleConflictCopy";
import MetaChip from "../UIToolkit/Badges/MetaChip";
import type { SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { QUICK_ADD_SOURCE } from "./quickAddConsts";
import { buildQuickAddStaffOptions, type QuickAddStaffOption } from "./quickAddStaffOptions";

type Params = {
	/** The day's scheduled shifts by person — empty in actual mode, which lists everyone once with no tags. */
	shiftsByNurse: ReadonlyMap<number, readonly NurseInShift[]>;
	applyPick: (option: QuickAddStaffOption | null, seed: QuickAddPickSeed | null) => void;
	windows: QuickAddPickWindows;
	seedWindow: (window: ShiftWindow) => void;
};

/**
 * The "+" dialog's staff picker: everyone active in the department, one row per same-day shift for anyone already
 * on the day (each tagged with that shift) and one plain row for everyone else; and what picking a row does — the
 * person, the job, and the times, from that row's shift when it has one (shown in the department's zone).
 */
export const useQuickAddPick = ({ shiftsByNurse, applyPick, windows, seedWindow }: Params) => {
	const { t } = useTranslation();
	const { hospitalData, chosenDepartment, timezone, showErrorSnackbar } = useHospitalData();

	const formatPersonName = useFormatPersonName();

	const staffOptions = useMemo(() => {
		const active = hospitalData.nurses.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id));
		return buildQuickAddStaffOptions(active, shiftsByNurse, formatPersonName);
	}, [hospitalData.nurses, chosenDepartment.department_id, shiftsByNurse, formatPersonName]);
	const optionByKey = useMemo(() => new Map(staffOptions.map((option) => [option.key, option])), [staffOptions]);

	/**
	 * The picker's row for an option it hands back (it hands back the base option type). Every option it can hand
	 * back comes from `staffOptions`, so a miss is a wiring bug: the pick reports it (below) and the chip renders
	 * nothing, rather than a throw that would be silent inside an event handler or crash the dialog mid-render.
	 */
	const rowFor = useCallback(
		(option: SystemAutoCompleteOption<Nurse>): QuickAddStaffOption | undefined => (option.key === undefined ? undefined : optionByKey.get(option.key)),
		[optionByKey],
	);

	const handleOptionChange = useCallback(
		(option: SystemAutoCompleteOption<Nurse> | null) => {
			const row = option ? rowFor(option) : null;
			if (row === undefined) {
				showErrorSnackbar(t("error.add.nurse"), { source: QUICK_ADD_SOURCE, reason: "picked_option_not_a_row", option_key: option?.key });
				return;
			}
			const seed = row ? resolvePickSeed({ pickedShift: row.shift, firstJobId: row.value.jobs?.[0]?.id, ...windows }) : null;
			applyPick(row, seed);
			if (seed?.window) seedWindow({ startTime: seed.window.startTime.setZone(timezone), endTime: seed.window.endTime.setZone(timezone) });
		},
		[rowFor, windows, applyPick, seedWindow, timezone, showErrorSnackbar, t],
	);

	const renderScheduledChip = useCallback(
		(option: SystemAutoCompleteOption<Nurse>): ReactNode => {
			const shift = rowFor(option)?.shift;
			if (!shift) return null;
			const testId = `quick-add-nurse-scheduled-chip-${shift.nurseId}-${shift.id}`;
			return <MetaChip label={scheduledChipLabel(shift, t, timezone)} tone="neutral" size="small" dataTestId={testId} />;
		},
		[rowFor, t, timezone],
	);

	return { staffOptions, handleOptionChange, renderScheduledChip };
};
